import { useState, useCallback, useEffect, type RefObject } from 'react';

interface ScrollArrowsState {
  isHovered: boolean;
  showLeftArrow: boolean;
  showRightArrow: boolean;
}

interface UseScrollArrowsOptions {
  scrollElementRef: RefObject<HTMLDivElement | null>;
  gamesLength: number;
  itemWidth: number;
  getPadding: () => { start: number; end: number };
  getGap: () => number;
}

interface UseScrollArrowsResult {
  scrollArrows: ScrollArrowsState;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  scrollLeft: () => void;
  scrollRight: () => void;
}

export function useScrollArrows({
  scrollElementRef,
  gamesLength,
  itemWidth,
  getPadding,
  getGap,
}: UseScrollArrowsOptions): UseScrollArrowsResult {
  const [scrollArrows, setScrollArrows] = useState<ScrollArrowsState>({
    isHovered: false,
    showLeftArrow: false,
    showRightArrow: false,
  });

  const updateArrowVisibility = useCallback(() => {
    if (!scrollArrows.isHovered || gamesLength === 0 || !scrollElementRef.current) {
      setScrollArrows((prev) => ({ ...prev, showLeftArrow: false, showRightArrow: false }));
      return;
    }

    const container = scrollElementRef.current;
    const padding = getPadding();
    const gap = getGap();
    const lastCardRightEdge = padding.start + gamesLength * itemWidth - gap;

    if (lastCardRightEdge <= container.offsetWidth + 10) {
      setScrollArrows((prev) => ({ ...prev, showLeftArrow: false, showRightArrow: false }));
      return;
    }

    const isAtLeftEdge = container.scrollLeft <= 1;
    const isLastCardVisible = container.scrollLeft + container.offsetWidth >= lastCardRightEdge - 5;

    setScrollArrows((prev) => ({
      ...prev,
      showLeftArrow: !isAtLeftEdge,
      showRightArrow: !isLastCardVisible,
    }));
  }, [scrollArrows.isHovered, gamesLength, itemWidth, getPadding, getGap, scrollElementRef]);

  const handleMouseEnter = useCallback(() => {
    setScrollArrows((prev) => ({ ...prev, isHovered: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setScrollArrows({ isHovered: false, showLeftArrow: false, showRightArrow: false });
  }, []);

  const scrollLeft = useCallback(() => {
    const container = scrollElementRef.current;
    if (!container || container.scrollLeft <= 0) return;

    const padding = getPadding().start;
    const firstFullyVisibleIndex = Math.ceil((container.scrollLeft - padding) / itemWidth);
    const targetIndex = Math.max(0, firstFullyVisibleIndex - 4);
    const newScrollLeft = targetIndex === 0 ? 0 : Math.max(0, padding + targetIndex * itemWidth - 5);

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    requestAnimationFrame(() => setTimeout(updateArrowVisibility, 300));
  }, [itemWidth, getPadding, updateArrowVisibility, scrollElementRef]);

  const scrollRight = useCallback(() => {
    const container = scrollElementRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.offsetWidth;
    if (container.scrollLeft >= maxScroll) return;

    const padding = getPadding().start;
    const viewportWidth = container.offsetWidth;
    const lastFullyVisibleIndex = Math.floor((container.scrollLeft + viewportWidth - padding) / itemWidth) - 1;
    const targetIndex = Math.min(lastFullyVisibleIndex + 3, gamesLength - 1);
    const targetRightEdge = padding + (targetIndex + 1) * itemWidth;

    let newScrollLeft = targetRightEdge - viewportWidth;
    newScrollLeft += targetIndex === gamesLength - 1 ? getPadding().end : 5;
    newScrollLeft = Math.min(maxScroll, Math.max(0, newScrollLeft));

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    requestAnimationFrame(() => setTimeout(updateArrowVisibility, 300));
  }, [itemWidth, getPadding, gamesLength, updateArrowVisibility, scrollElementRef]);

  useEffect(() => {
    updateArrowVisibility();
  }, [updateArrowVisibility]);

  useEffect(() => {
    const container = scrollElementRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (scrollArrows.isHovered) updateArrowVisibility();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollArrows.isHovered, updateArrowVisibility, scrollElementRef]);

  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (!scrollArrows.isHovered) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => requestAnimationFrame(updateArrowVisibility), 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [scrollArrows.isHovered, updateArrowVisibility]);

  return { scrollArrows, handleMouseEnter, handleMouseLeave, scrollLeft, scrollRight };
}
